import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  PackageCheck,
  RefreshCw,
  Truck,
  CheckCircle2,
  AlertTriangle,
  Boxes,
  Building2,
  FileText,
  MapPin,
  Sparkles,
  ArrowRight,
  ExternalLink,
} from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatNumber, formatMoney } from "@/lib/utils";
import EntityLink from "@/components/shared/EntityLink";
import { useCustomerLookup } from "@/hooks/useFKLookup";
import { useHeaderSearch } from "@/hooks/useHeaderSearch";

export function ReadyToDispatchTab() {
  const navigate = useNavigate();
  const { headerSearch } = useHeaderSearch();
  const { options: customerOptions } = useCustomerLookup();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("ALL");
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: [
      "ready-to-dispatch-queue",
      headerSearch,
      selectedCustomerId === "ALL" ? undefined : selectedCustomerId,
    ],
    queryFn: async () => {
      return (await trpcClient.deliveries.getReadyToDispatchQueue.query({
        search: headerSearch.trim() || undefined,
        customerId:
          selectedCustomerId === "ALL" ? undefined : selectedCustomerId,
      })) as {
        batches: Array<{
          qcFId: string | null;
          finalQcId: string | null;
          qcStatus: string;
          checkTime: string | Date | null;
          inspectorName: string | null;
          poId: string | null;
          poCode: string | null;
          soId: string | null;
          soCode: string | null;
          orderDetailId: string | null;
          customerId: string | null;
          customerName: string | null;
          customerShortName: string | null;
          productId: string | null;
          productCode: string | null;
          productName: string | null;
          unit: string | null;
          unitPrice: number;
          totalQty: number;
          deliveredQty: number;
          qtyRemaining: number;
          deliveryAddress: string | null;
          cartonStats: {
            total: number;
            dispatched: number;
            available: number;
            availableLabels: string[];
          };
        }>;
        total: number;
      };
    },
  });

  const batches = data?.batches || [];

  // Key generator for robust selection
  const getItemKey = (b: any, idx: number) => {
    return b.orderDetailId || b.qcFId || `${b.poId || b.soId || ""}-${b.productId || ""}-${idx}`;
  };

  // Metrics summary
  const metrics = useMemo(() => {
    let fqcCount = 0;
    let packagedCount = 0;
    let totalQtyRemaining = 0;

    batches.forEach((b) => {
      if (b.qcFId || (b.inspectorName && b.inspectorName !== "Đóng gói")) {
        fqcCount++;
      } else {
        packagedCount++;
      }
      totalQtyRemaining += b.qtyRemaining || 0;
    });

    return {
      total: batches.length,
      fqcCount,
      packagedCount,
      totalQtyRemaining,
    };
  }, [batches]);

  // Selection handlers
  const handleToggleSelect = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const all = new Set(batches.map((b, idx) => getItemKey(b, idx)));
      setSelectedKeys(all);
    } else {
      setSelectedKeys(new Set());
    }
  };

  // Selected batches analysis
  const selectedBatches = useMemo(() => {
    return batches.filter((b, idx) => selectedKeys.has(getItemKey(b, idx)));
  }, [batches, selectedKeys]);

  const customerGroup = useMemo(() => {
    const custMap = new Map<string, string>();
    selectedBatches.forEach((b) => {
      if (b.customerId) {
        custMap.set(
          b.customerId,
          b.customerShortName || b.customerName || b.customerId
        );
      }
    });
    return custMap;
  }, [selectedBatches]);

  const isMultipleCustomers = customerGroup.size > 1;
  const singleCustomerName =
    customerGroup.size === 1 ? Array.from(customerGroup.values())[0] : "";
  const singleCustomerId =
    customerGroup.size === 1 ? Array.from(customerGroup.keys())[0] : null;

  // Single item create delivery
  const handleCreateSingle = (batch: any) => {
    const detailIds = batch.orderDetailId || "";
    const fqcBatchIds = batch.qcFId || "";
    navigate({
      to: "/delivery/create",
      search: {
        customerId: batch.customerId || "",
        orderId: batch.soId || "",
        detailIds: detailIds,
        fqcBatchIds: fqcBatchIds,
      } as any,
    });
  };

  // Bulk create delivery for selected items of same customer
  const handleCreateBatchDelivery = () => {
    if (selectedBatches.length === 0 || isMultipleCustomers) return;

    const detailIds = selectedBatches
      .map((b) => b.orderDetailId)
      .filter(Boolean)
      .join(",");
    const fqcBatchIds = selectedBatches
      .map((b) => b.qcFId)
      .filter(Boolean)
      .join(",");

    const allSameOrder = selectedBatches.every(
      (b) => b.soId && b.soId === selectedBatches[0].soId
    );
    const orderId = allSameOrder ? selectedBatches[0].soId : "";

    navigate({
      to: "/delivery/create",
      search: {
        customerId: singleCustomerId || "",
        orderId: orderId || "",
        detailIds: detailIds,
        fqcBatchIds: fqcBatchIds,
      } as any,
    });
  };

  const allSelected =
    batches.length > 0 && selectedKeys.size === batches.length;
  const isIndeterminate =
    selectedKeys.size > 0 && selectedKeys.size < batches.length;

  return (
    <div className="space-y-3 p-3">
      {/* 1. Single-Row Inline Metrics Bar (Rule 4: 36-38px) */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5 bg-white border border-slate-200/90 rounded-lg shadow-xs text-xs">
        <div className="flex items-center gap-3 overflow-x-auto py-0.5">
          <div className="flex items-center gap-1.5 font-medium text-slate-700">
            <Boxes className="w-3.5 h-3.5 text-indigo-600" />
            <span>Sẵn sàng giao:</span>
            <span className="px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-bold border border-indigo-200">
              {metrics.total} mặt hàng
            </span>
          </div>
          <span className="text-slate-300">|</span>
          <div className="flex items-center gap-1.5 text-slate-600">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            <span>KCS FQC Đạt:</span>
            <span className="font-semibold text-emerald-700">
              {metrics.fqcCount}
            </span>
          </div>
          <span className="text-slate-300">|</span>
          <div className="flex items-center gap-1.5 text-slate-600">
            <PackageCheck className="w-3.5 h-3.5 text-amber-600" />
            <span>Đã đóng gói:</span>
            <span className="font-semibold text-amber-700">
              {metrics.packagedCount}
            </span>
          </div>
          <span className="text-slate-300">|</span>
          <div className="flex items-center gap-1.5 text-slate-600">
            <Truck className="w-3.5 h-3.5 text-blue-600" />
            <span>Tổng sản lượng:</span>
            <span className="font-bold text-slate-900">
              {formatNumber(metrics.totalQtyRemaining)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading || isRefetching}
            className="h-7 text-xs text-slate-600 hover:text-slate-900 px-2"
          >
            <RefreshCw
              size={12}
              className={`mr-1 ${isRefetching ? "animate-spin text-indigo-600" : ""}`}
            />
            Làm mới
          </Button>
        </div>
      </div>

      {/* 2. Customer Filter & Action Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 bg-slate-50/70 p-2 rounded-lg border border-slate-200">
        <div className="flex flex-wrap items-center gap-2.5">
          <label className="text-xs font-medium text-slate-600">Lọc khách hàng:</label>
          <select
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
            className="h-8 text-xs bg-white border border-slate-200 rounded-md px-2.5 text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="ALL">-- Tất cả khách hàng ({customerOptions.length}) --</option>
            {customerOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>

          {headerSearch && (
            <span className="text-xs text-slate-500 italic bg-white px-2 py-1 rounded border border-slate-200">
              Từ khóa tìm kiếm: "{headerSearch}"
            </span>
          )}
        </div>

        {/* Multi-select Action or Warning */}
        <div className="flex items-center gap-2">
          {isMultipleCustomers && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-rose-50 text-rose-700 border border-rose-200 text-xs">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              <span>Chỉ chọn các mặt hàng của cùng 1 khách hàng!</span>
            </div>
          )}

          {selectedBatches.length > 0 && !isMultipleCustomers && (
            <Button
              size="sm"
              onClick={handleCreateBatchDelivery}
              className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-xs animate-in fade-in"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5 text-emerald-200" />
              <span>
                Lập Phiếu Giao Hàng ({selectedBatches.length} mặt hàng -{" "}
                {singleCustomerName})
              </span>
            </Button>
          )}
        </div>
      </div>

      {/* 3. High-Density Enterprise Data Grid (Rule 4) */}
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-xs">
        <div className="overflow-x-auto max-h-[calc(100vh-270px)]">
          <Table className="relative w-full text-xs">
            <TableHeader className="bg-slate-50 sticky top-0 z-30 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <TableRow className="border-b border-slate-200">
                <TableHead className="w-10 px-3 py-2 text-center sticky left-0 z-40 bg-slate-50 border-r border-slate-200">
                  <Checkbox
                    checked={allSelected ? true : isIndeterminate ? "indeterminate" : false}
                    onCheckedChange={handleSelectAll}
                    aria-label="Chọn tất cả"
                  />
                </TableHead>
                <TableHead className="w-[180px] px-3 py-2 text-left font-semibold text-slate-700 sticky left-10 z-40 bg-slate-50 border-r-2 border-slate-300 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.06)]">
                  Khách hàng
                </TableHead>
                <TableHead className="w-[120px] px-3 py-2 text-left font-semibold text-slate-700">
                  Đơn hàng / LSX
                </TableHead>
                <TableHead className="min-w-[220px] px-3 py-2 text-left font-semibold text-slate-700">
                  Sản phẩm
                </TableHead>
                <TableHead className="w-[110px] px-3 py-2 text-center font-semibold text-slate-700">
                  Nguồn KCS
                </TableHead>
                <TableHead className="w-[60px] px-3 py-2 text-center font-semibold text-slate-700">
                  ĐVT
                </TableHead>
                <TableHead className="w-[85px] px-3 py-2 text-right font-semibold text-slate-700">
                  SL Đặt
                </TableHead>
                <TableHead className="w-[85px] px-3 py-2 text-right font-semibold text-slate-700">
                  Đã giao
                </TableHead>
                <TableHead className="w-[95px] px-3 py-2 text-right font-bold text-emerald-800 bg-emerald-50/50">
                  Sẵn sàng giao
                </TableHead>
                <TableHead className="w-[90px] px-3 py-2 text-right font-semibold text-slate-700">
                  Đơn giá
                </TableHead>
                <TableHead className="w-[110px] px-3 py-2 text-right font-semibold text-slate-700">
                  Dự kiến tiền
                </TableHead>
                <TableHead className="min-w-[150px] px-3 py-2 text-left font-semibold text-slate-700">
                  Địa chỉ giao
                </TableHead>
                <TableHead className="w-[110px] px-3 py-2 text-right sticky right-0 z-30 bg-slate-50 border-l border-slate-200">
                  Thao tác
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={13} className="h-10 text-center text-slate-400">
                      Đang tải danh sách hàng chờ giao...
                    </TableCell>
                  </TableRow>
                ))
              ) : batches.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={13}
                    className="h-36 text-center text-slate-500 py-8"
                  >
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="p-3 bg-emerald-50 rounded-full text-emerald-600">
                        <PackageCheck size={28} />
                      </div>
                      <p className="font-medium text-slate-700">
                        Hiện không có mặt hàng nào tồn đọng chờ giao
                      </p>
                      <p className="text-xs text-slate-400 max-w-md">
                        Tất cả các lô thành phẩm KCS FQC Đạt và đơn hàng đóng gói đã được lập phiếu giao hàng hoặc chuyển sang vận chuyển.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                batches.map((batch, idx) => {
                  const key = getItemKey(batch, idx);
                  const isSelected = selectedKeys.has(key);
                  const isFqc = Boolean(batch.qcFId);
                  const estimatedAmount =
                    (batch.qtyRemaining || 0) * (batch.unitPrice || 0);

                  return (
                    <TableRow
                      key={key}
                      className={`hover:bg-slate-50/80 transition-colors ${
                        isSelected ? "bg-indigo-50/60" : ""
                      }`}
                    >
                      {/* Col 0: Checkbox (Solid Sticky left-0, Rule 4) */}
                      <TableCell className="px-3 py-2 text-center sticky left-0 z-20 bg-white border-r border-slate-200">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleToggleSelect(key)}
                          aria-label={`Chọn ${batch.productName}`}
                        />
                      </TableCell>

                      {/* Col 1: Customer (Solid Sticky left-10, Rule 4) */}
                      <TableCell className="px-3 py-2 font-medium text-slate-900 sticky left-10 z-20 bg-white border-r-2 border-slate-300 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.06)]">
                        <div className="truncate font-semibold text-slate-900" title={batch.customerName || ""}>
                          {batch.customerShortName || batch.customerName || "—"}
                        </div>
                        {batch.customerShortName && batch.customerName && (
                          <div className="text-[11px] text-slate-500 truncate" title={batch.customerName}>
                            {batch.customerName}
                          </div>
                        )}
                      </TableCell>

                      {/* Col 2: SO / PO Codes */}
                      <TableCell className="px-3 py-2">
                        {batch.soCode && (
                          <div className="font-mono font-medium text-indigo-700">
                            <EntityLink entityType="order" id={batch.soId || ""} code={batch.soCode} />
                          </div>
                        )}
                        {batch.poCode && (
                          <div className="font-mono text-[11px] text-slate-500">
                            <EntityLink entityType="productionOrder" id={batch.poId || ""} code={batch.poCode} />
                          </div>
                        )}
                      </TableCell>

                      {/* Col 3: Product Code & Name */}
                      <TableCell className="px-3 py-2">
                        <div className="font-medium text-slate-900 line-clamp-1" title={batch.productName || ""}>
                          {batch.productName || "—"}
                        </div>
                        {batch.productCode && (
                          <div className="font-mono text-[11px] text-slate-400">
                            {batch.productCode}
                          </div>
                        )}
                      </TableCell>

                      {/* Col 4: KCS Source Badge */}
                      <TableCell className="px-3 py-2 text-center">
                        {isFqc ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] font-semibold whitespace-nowrap">
                            ✓ KCS FQC Đạt
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] font-semibold whitespace-nowrap">
                            📦 Đã Đóng Gói
                          </Badge>
                        )}
                      </TableCell>

                      {/* Col 5: Unit */}
                      <TableCell className="px-3 py-2 text-center text-slate-600">
                        {batch.unit || "Hộp"}
                      </TableCell>

                      {/* Col 6: Total Quantity */}
                      <TableCell className="px-3 py-2 text-right font-mono text-slate-600">
                        {formatNumber(batch.totalQty)}
                      </TableCell>

                      {/* Col 7: Delivered Quantity */}
                      <TableCell className="px-3 py-2 text-right font-mono text-slate-500">
                        {formatNumber(batch.deliveredQty)}
                      </TableCell>

                      {/* Col 8: Ready to Dispatch Quantity (Highlighted) */}
                      <TableCell className="px-3 py-2 text-right font-mono font-bold text-emerald-700 bg-emerald-50/40">
                        {formatNumber(batch.qtyRemaining)}
                      </TableCell>

                      {/* Col 9: Unit Price */}
                      <TableCell className="px-3 py-2 text-right font-mono text-slate-600">
                        {batch.unitPrice > 0 ? formatNumber(batch.unitPrice) : "—"}
                      </TableCell>

                      {/* Col 10: Estimated Total Amount */}
                      <TableCell className="px-3 py-2 text-right font-mono font-medium text-slate-800">
                        {estimatedAmount > 0 ? formatMoney(estimatedAmount) : "—"}
                      </TableCell>

                      {/* Col 11: Delivery Address */}
                      <TableCell className="px-3 py-2 text-slate-600">
                        <div className="truncate max-w-[200px]" title={batch.deliveryAddress || ""}>
                          {batch.deliveryAddress || "Theo địa chỉ khách hàng"}
                        </div>
                      </TableCell>

                      {/* Col 12: Actions (Solid Sticky right-0) */}
                      <TableCell className="px-3 py-2 text-right sticky right-0 z-20 bg-white border-l border-slate-200">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCreateSingle(batch)}
                          className="h-7 text-xs px-2 text-indigo-700 border-indigo-200 hover:bg-indigo-50 font-medium"
                        >
                          <Truck className="w-3 h-3 mr-1 text-indigo-600" />
                          <span>Lập phiếu</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
